import { defaultHeaderModules } from './header-modules.js';
import { blocksFromLegacyModules } from './header-blocks.js';
import { BUILTIN_FACES } from '../library/builtins.js';
import { CUSTOM_FONT_PREFIX } from '../library/schema.js';
import { cssForTheme, resolveFace } from '../library/resolve.js';

/**
 * Theme defaults.
 *
 * These values are a deliberate clone of what the sites render today, so a site
 * with no saved theme looks byte-identical after the theme engine lands. Every
 * number here was read off the current implementation:
 *
 * - colours: DEFAULT_THEME_COLORS that lib/server.js and lib/client.js both had
 * - logo: components/layouts/Header.js masks /logo.svg with --color-text at
 *   160px x 3.36em on desktop and 140px x 32px on mobile
 * - nav: font-sansumi, 1em, uppercase, tracking 0.2em, 1em gap between links
 * - header padding: 22px top and bottom
 * - fonts: the --font-body / --font-display vars from styles/globals.css
 *
 * Changing a default here changes how every un-themed site renders, so don't.
 * New looks belong in a draft that someone publishes.
 */

/** Built-in faces. Uploaded faces live in the site library, not on the theme. */
export const FONT_OPTIONS = BUILTIN_FACES;

export const CASING_OPTIONS = [
    { id: 'none', label: 'As typed' },
    { id: 'uppercase', label: 'UPPERCASE' },
    { id: 'lowercase', label: 'lowercase' },
    { id: 'capitalize', label: 'Title Case' },
];

export const LOGO_MODES = [
    // The legacy default: the SVG is used as a mask and filled with the text colour.
    { id: 'mask', label: 'Mask (tinted with text colour)' },
    { id: 'image', label: 'Image (original colours)' },
    { id: 'text', label: 'Text wordmark' },
];

export const NAV_SOURCES = [
    { id: 'all-public', label: 'Every public page' },
    { id: 'root-public', label: 'Top-level pages only' },
];

export const DEFAULT_THEME = {
    colors: {
        background: '#ffffff',
        text: '#1e293b',
        sideMenuText: '#0f172a',
        sideMenuBackground: '#e11d48',
    },

    fonts: {
        body: 'work-sans',
        heading: 'work-sans',
        nav: 'sansumi',
        wordmark: 'sansumi',
    },

    type: {
        // styles/globals.css sets body { font-size: 11px; line-height: 1.6 }
        bodySize: 11,
        bodyLineHeight: 1.6,
    },

    brand: {
        siteTitle: '',
        metaTitle: '',
        headerTitle: '',
        logo: {
            mode: 'mask',
            // Served from the site's static assets; the legacy hardcoded path.
            src: '/logo.svg',
            fileId: null,
            /** Image viewer / media pages. Null = use page logo. */
            mediaFileId: null,
            /** Slideshow intro. Null = use page logo. */
            slideshowFileId: null,
            text: '',
            hrefPageId: null,
        },
        favicon: { fileId: null, version: null },
        ogImage: { fileId: null },
        customFonts: [],
    },

    header: {
        // Typed free-floating blocks (Brand + Menu). Set after object build.
        blocks: [],
        // Logo and links module map (viewer chrome + dual-write during transition).
        modules: defaultHeaderModules(),
        padY: 22,
        logoWidth: 160,
        logoHeight: 3.36,
        logoWidthMobile: 140,
        logoHeightMobile: 32,
        navSize: 1,
        navTracking: 0.2,
        navCasing: 'uppercase',
        navGap: 1,
        navPadX: 1.6,
        navSource: 'all-public',
    },

    menu: {
        navSource: 'root-public',
        navCasing: 'uppercase',
    },

    chrome: {
        // Masonry description overlay and similar page furniture.
        captionTracking: 0.25,
        captionCasing: 'uppercase',
    },

    // Centered column shared by galleries and the header. 1600px is the old hardcoded width.
    layout: {
        pageWidth: 1600,
    },

    // Sparse per-breakpoint overrides, e.g. { phone: { 'header.padY': 12 } }
    overrides: { phone: {}, tablet: {} },

    icons: { set: 'default' },
};

// Default Brand + Menu from the classic modules + nav tokens.
DEFAULT_THEME.header.blocks = blocksFromLegacyModules(DEFAULT_THEME);

export { CUSTOM_FONT_PREFIX };

/** @param {string} id @param {Object} [theme] @param {Object} [library] */
export function getFontOption(id, theme, library) {
    const faces = [...(library?.faces || [])];
    (theme?.brand?.customFonts || []).forEach((font) => {
        if (font?.id && !faces.some((face) => face.id === font.id)) faces.push(font);
    });
    const resolved = resolveFace(id, { faces, iconSets: library?.iconSets || [] });
    return { ...resolved, custom: resolved.face };
}

/** @param {Object} theme @param {Object} [library] */
export function usedCustomFonts(theme, library) {
    return cssForTheme(theme, library).faces;
}

/** @param {Object} theme @param {Object} [library] */
export function googleFontsHref(theme, library) {
    return cssForTheme(theme, library).googleHref;
}

export default DEFAULT_THEME;
